import React, { useEffect, useMemo } from 'react';
import {
  HashRouter, Routes, Route, Navigate, useNavigate, useLocation,
} from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { App as CapacitorApp } from '@capacitor/app';
import { AppProvider, useApp } from './state/AppContext';
import { buildTheme } from './theme/buildTheme';
import { connectMqtt } from './mqtt/mqttClient';

import AppLayout from './components/AppLayout';
import LoginScreen from './pages/LoginScreen';
import OtpScreen from './pages/OtpScreen';
import DesktopShell from './pages/DesktopShell';
import RoomsScreen from './pages/RoomsScreen';
import DeviceScreen from './pages/device/DeviceScreen';
import SchedulesScreen from './pages/SchedulesScreen';
import FavouritesScreen from './pages/FavouritesScreen';
import ScenesScreen from './pages/ScenesScreen';
import RoomScenesScreen from './pages/scenes/RoomScenesScreen';
import ProfileScreen from './pages/ProfileScreen';
import CompaniesScreen from './pages/CompaniesScreen';
import DeviceTypesScreen from './pages/DeviceTypesScreen';
import WallModeScreen from './pages/wall/WallModeScreen';
import WallDesignerScreen from './pages/wall/WallDesignerScreen';
import AboutScreen from './pages/AboutScreen';

function Protected({ children }) {
  const { isAuthed } = useApp();
  return isAuthed ? children : <Navigate to="/login" replace />;
}

// Android hardware back: navigate within the SPA instead of letting the
// webview exit the app. At a root screen, exit. (No-op on web.)
function HardwareBack() {
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    let handle;
    CapacitorApp.addListener('backButton', () => {
      const root = ['/home', '/login'].includes(location.pathname);
      if (root) CapacitorApp.exitApp();
      else navigate(-1);
    }).then((h) => { handle = h; }).catch(() => {});
    return () => { try { handle && handle.remove(); } catch (e) { /* ignore */ } };
  }, [navigate, location.pathname]);
  return null;
}

function Shell() {
  const { user, isAuthed, isSuperAdmin } = useApp();
  const theme = useMemo(() => buildTheme(user), [user]);

  // Connect MQTT once the user is logged in so live control/status work.
  useEffect(() => {
    if (isAuthed) connectMqtt();
  }, [isAuthed]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <HashRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <HardwareBack />
        <Routes>
          <Route path="/login" element={<LoginScreen />} />
          <Route path="/otp" element={<OtpScreen />} />

          {/* Kiosk full-screen — deliberately without the app header. */}
          <Route path="/wall" element={<Protected><WallModeScreen /></Protected>} />
          <Route path="/wall/design" element={<Protected><WallDesignerScreen /></Protected>} />

          {/* Everything else renders under the persistent header. */}
          <Route element={<Protected><AppLayout /></Protected>}>
            <Route path="/home" element={<DesktopShell />} />
            <Route path="/rooms/:locationId" element={<RoomsScreen />} />
            <Route path="/room/:roomId/scenes" element={<RoomScenesScreen />} />
            <Route path="/device/:uniqueNumber" element={<DeviceScreen />} />
            <Route path="/schedule/:uniqueNumber/:output" element={<SchedulesScreen />} />
            <Route path="/favourites" element={<FavouritesScreen />} />
            <Route path="/scenes" element={<ScenesScreen />} />
            <Route path="/profile" element={<ProfileScreen />} />
            <Route path="/about" element={<AboutScreen />} />
            <Route
              path="/companies"
              element={isSuperAdmin ? <CompaniesScreen /> : <Navigate to="/home" replace />}
            />
            <Route
              path="/device-types"
              element={isSuperAdmin ? <DeviceTypesScreen /> : <Navigate to="/home" replace />}
            />
          </Route>

          <Route path="*" element={<Navigate to={isAuthed ? '/home' : '/login'} replace />} />
        </Routes>
      </HashRouter>
    </ThemeProvider>
  );
}

export default function App() {
  return (
    <AppProvider>
      <Shell />
    </AppProvider>
  );
}
